import { useFetcher } from '@remix-run/react'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { CurrencyInput, NumberInput } from '~/components/currency-input'
import { makeShopOwnPath } from '~/helpers/routes'
import { useShop } from '~/hooks/data/use-shop'
import type {
  ShopPromotions as ShopPromotionsType,
  ShopPromotion,
} from '~/schemas/shop'
import { shopPromotionSchema } from '~/schemas/shop'
import { Alert, Button, Dialog, Divider, Switch } from '~/ui'
import { cn } from '~/utils/classnames'
import { getPromotionLabel } from '~/utils/promotion'

interface PromotionsFormProps {
  onSuccessfulSubmit?: () => void
}

type Promotion = Partial<ShopPromotion> | null

// Allow string values while typing
type PartialShopPromotion = {
  type: 'xPercentOff'
  amount: number | string
  minSpend?: number | string
}

type PartialShippingPromotion = {
  type: 'freeShipping'
  minSpend?: number | string
}

export const PromotionsForm = ({ onSuccessfulSubmit }: PromotionsFormProps) => {
  const { t } = useTranslation()
  const fetcher = useFetcher()
  const shop = useShop()

  const [shopPromotion, setShopPromotion] =
    useState<PartialShopPromotion | null>(() => {
      const promotion = (shop.promotions as ShopPromotionsType)?.find(
        (p) => p.type === 'xPercentOff'
      )
      if (!promotion) return null

      // Convert minSpend from cents to euros for editing
      return {
        ...promotion,
        minSpend: promotion.minSpend ? promotion.minSpend / 100 : undefined,
      }
    })

  // Helper schemas for coercion and conversion
  const coerceShopPromotionSchema = z.object({
    type: z.literal('xPercentOff'),
    amount: z.coerce.number().catch(0),
    minSpend: z.coerce
      .number()
      .transform((val) => Math.round(val * 100)) // Convert euros to cents
      .catch(0)
      .optional(),
  })

  const coerceShippingPromotionSchema = z.object({
    type: z.literal('freeShipping'),
    minSpend: z.coerce
      .number()
      .transform((val) => Math.round(val * 100)) // Convert euros to cents
      .catch(0)
      .optional(),
  })

  const shopPromotionMessage = usePromotionMessage(
    shopPromotion ? coerceShopPromotionSchema.parse(shopPromotion) : null
  )

  const [shippingPromotion, setShippingPromotion] =
    useState<PartialShippingPromotion | null>(() => {
      const promotion = (shop.promotions as ShopPromotionsType)?.find(
        (p) => p.type === 'freeShipping'
      )
      if (!promotion) return null

      // Convert minSpend from cents to euros for editing
      return {
        ...promotion,
        minSpend: promotion.minSpend ? promotion.minSpend / 100 : undefined,
      }
    })

  const shippingPromotionMessage = usePromotionMessage(
    shippingPromotion
      ? coerceShippingPromotionSchema.parse(shippingPromotion)
      : null
  )

  const [errorKey, setErrorKey] = useState<number>(0)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const promotions: ShopPromotion[] = []

    if (shopPromotion) {
      // Coerce strings to numbers with Zod
      const coercedShopPromotion =
        coerceShopPromotionSchema.parse(shopPromotion)

      if (safeValidationPromotion(coercedShopPromotion)) {
        promotions.push(coercedShopPromotion as ShopPromotion)
      } else {
        return setErrorKey((prev) => prev + 1)
      }
    }

    if (shippingPromotion) {
      // Coerce strings to numbers with Zod
      const coercedShippingPromotion =
        coerceShippingPromotionSchema.parse(shippingPromotion)

      if (safeValidationPromotion(coercedShippingPromotion)) {
        promotions.push(coercedShippingPromotion as ShopPromotion)
      } else {
        return setErrorKey((prev) => prev + 1)
      }
    }

    fetcher.submit(JSON.stringify({ promotions }), {
      encType: 'application/json',
      method: 'PATCH',
      action: makeShopOwnPath(shop, '/?index'),
    })
  }

  useEffect(() => {
    // Check if submit was successful, if it was close the modal
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((fetcher.data as unknown as any)?.success) {
      onSuccessfulSubmit?.()
    }
  }, [fetcher, onSuccessfulSubmit])

  return (
    <form
      onSubmit={handleSubmit}
      className="flex w-full flex-col gap-4 py-3 text-xs"
    >
      <div>
        <FieldsetToggle
          checked={!!shopPromotion}
          onCheckedChange={(checked) =>
            setShopPromotion(
              checked ? { type: 'xPercentOff', amount: 10, minSpend: 0 } : null
            )
          }
        >
          {t('components.manage_discounts.shop_discount')}
        </FieldsetToggle>

        <AnimatePresence initial={false}>
          {!!shopPromotion && (
            <Fieldset>
              <PercentageField
                name="shop-amount"
                label={t('components.manage_discounts.percentage')}
                initialValue={shopPromotion.amount}
                onChange={(value) =>
                  setShopPromotion((prev) =>
                    prev ? { ...prev, amount: value } : null
                  )
                }
              />

              <CurrencyField
                name="shop-min-spend"
                label={t('components.manage_discounts.minimum_spend')}
                initialValue={shopPromotion.minSpend ?? 0}
                onChange={(value) => {
                  setShopPromotion((prev) =>
                    prev ? { ...prev, minSpend: value } : null
                  )
                }}
              />

              {shopPromotionMessage.message && (
                <Alert
                  type={shopPromotionMessage.valid ? 'over-media' : 'error'}
                  title={shopPromotionMessage.message}
                />
              )}
            </Fieldset>
          )}
        </AnimatePresence>
      </div>

      <Divider className="bg-border/4" />

      <div>
        <FieldsetToggle
          checked={!!shippingPromotion}
          onCheckedChange={(checked) =>
            setShippingPromotion(
              checked ? { type: 'freeShipping', minSpend: 0 } : null
            )
          }
        >
          {t('components.manage_discounts.free_shipping')}
        </FieldsetToggle>

        <AnimatePresence initial={false}>
          {!!shippingPromotion && (
            <Fieldset>
              <CurrencyField
                name="shipping-min-spend"
                label={t('components.manage_discounts.minimum_spend')}
                initialValue={shippingPromotion.minSpend ?? 0}
                onChange={(value) => {
                  setShippingPromotion((prev) =>
                    prev ? { ...prev, minSpend: value } : null
                  )
                }}
              />

              {shippingPromotionMessage.message && (
                <Alert
                  type={shippingPromotionMessage.valid ? 'over-media' : 'error'}
                  title={shippingPromotionMessage.message}
                />
              )}
            </Fieldset>
          )}
        </AnimatePresence>
      </div>

      <Dialog.Actions className="mt-auto flex w-full py-0">
        <Button
          type="submit"
          key={errorKey}
          isLoading={fetcher.state !== 'idle'}
          className={cn('w-full md:w-fit', errorKey > 0 && 'animate-shake')}
        >
          Done
        </Button>
      </Dialog.Actions>
    </form>
  )
}

interface FieldsetToggleProps {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  children: React.ReactNode
}

const FieldsetToggle = ({
  checked,
  onCheckedChange,
  children,
}: FieldsetToggleProps) => {
  return (
    <label className="flex items-center justify-between gap-2 px-4">
      <span className="font-medium">{children}</span>
      <Switch
        checked={checked}
        onChange={(event) => onCheckedChange(event.target.checked)}
      />
    </label>
  )
}

interface FieldsetProps {
  children: React.ReactNode
}

const Fieldset = ({ children }: FieldsetProps) => {
  return (
    <motion.div
      className="-ml-0.5 box-content flex flex-col gap-4 overflow-hidden px-4 [&>*:first-child]:pt-4"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  )
}

interface CurrencyFieldProps {
  label: string
  name: string
  initialValue?: number | string
  onChange: (value: string) => void
}

const CurrencyField = ({
  label,
  name,
  initialValue,
  onChange,
}: CurrencyFieldProps) => {
  const id = useId()

  return (
    <div>
      <label className="text-foreground/64" htmlFor={id}>
        {label}
      </label>

      <div className="mt-2">
        <CurrencyInput
          id={id}
          name={name}
          placeholder="0"
          min={0}
          value={
            typeof initialValue === 'string'
              ? initialValue
              : initialValue === 0
                ? ''
                : initialValue
          }
          onChange={onChange}
        />
      </div>
    </div>
  )
}

const usePromotionMessage = (promotion: Promotion | null) => {
  const { t } = useTranslation()

  return useMemo(() => {
    if (!promotion) {
      return {
        valid: false,
        message: t('components.manage_discounts.invalid_promotion'),
      }
    }

    const result = shopPromotionSchema.safeParse(promotion)

    if (result.success) {
      return {
        valid: true,
        message: getPromotionLabel(result.data, t),
      }
    } else {
      // Get the first validation error and use its message from the schema
      const firstError = result.error.errors?.[0]
      const errorMessage = firstError?.message

      // If the error message is a translation key (starts with 'errors.'), use it
      // Otherwise fall back to generic error messages
      if (errorMessage?.startsWith('errors.')) {
        return {
          valid: false,
          message: t(errorMessage),
        }
      }

      // Fallback to field-based generic errors
      const field = firstError?.path?.[0]
      let errorKey = 'invalid_promotion'
      if (field === 'amount') {
        errorKey = 'amount_error'
      } else if (field === 'minSpend') {
        errorKey = 'min_spend_error'
      }

      return {
        valid: false,
        message: t(`components.manage_discounts.${errorKey}`),
      }
    }
  }, [promotion, t])
}

interface PercentageFieldProps {
  label: string
  name: string
  initialValue?: number | string
  onChange: (value: string) => void
}

const PercentageField = ({
  label,
  name,
  initialValue,
  onChange,
}: PercentageFieldProps) => {
  const id = useId()

  return (
    <div>
      <label className="text-foreground/64" htmlFor={id}>
        {label}
      </label>

      <div className="mt-2">
        <NumberInput
          id={id}
          name={name}
          placeholder="0"
          min={0}
          max={100}
          allowDecimal={false}
          suffix="%"
          value={
            typeof initialValue === 'string'
              ? initialValue
              : initialValue === 0
                ? ''
                : initialValue
          }
          onChange={onChange}
        />
      </div>
    </div>
  )
}

const safeValidationPromotion = (
  promotion: Partial<ShopPromotion> | null
): promotion is ShopPromotion => {
  try {
    const result = shopPromotionSchema.safeParse(promotion)
    return result.success
  } catch (error) {
    return false
  }
}
